import { useEffect, useState } from 'react'
import { X, Bell, TrendingUp, Bot, AlertTriangle } from 'lucide-react'
import { useLiveAlerts, useLiveTrades } from '../contexts/WebSocketContext'
import { cn } from '../lib/utils'
import { formatCurrency } from '../lib/utils'

interface Toast {
  id: string
  type: 'alert' | 'trade' | 'bot'
  title: string
  message: string
  data?: any
}

export function RealTimeToast() {
  const [toasts, setToasts] = useState<Toast[]>([])
  const latestAlert = useLiveAlerts()
  const latestTrade = useLiveTrades()

  // Handle new alerts
  useEffect(() => {
    if (!latestAlert) return

    const newToast: Toast = {
      id: `alert-${latestAlert.id}-${Date.now()}`,
      type: 'alert',
      title: latestAlert.title || 'New Alert',
      message: latestAlert.message,
      data: latestAlert,
    }

    addToast(newToast)
  }, [latestAlert])

  // Handle new trades
  useEffect(() => {
    if (!latestTrade) return

    const newToast: Toast = {
      id: `trade-${latestTrade.id}-${Date.now()}`,
      type: 'trade',
      title: 'Trade Executed',
      message: `${latestTrade.side} ${formatCurrency(latestTrade.amount)} at ${(latestTrade.price * 100).toFixed(1)}¢`,
      data: latestTrade,
    }

    addToast(newToast)
  }, [latestTrade])

  const addToast = (toast: Toast) => {
    setToasts((prev) => [...prev, toast])
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      removeToast(toast.id)
    }, 5000)
  }

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }

  if (toasts.length === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'bg-white rounded-lg shadow-lg border-l-4 p-4 min-w-[300px] max-w-md animate-in slide-in-from-right',
            toast.type === 'alert' && 'border-blue-500',
            toast.type === 'trade' && 'border-green-500',
            toast.type === 'bot' && 'border-purple-500'
          )}
        >
          <div className="flex items-start">
            <div
              className={cn(
                'p-2 rounded-full mr-3',
                toast.type === 'alert' && 'bg-blue-100',
                toast.type === 'trade' && 'bg-green-100',
                toast.type === 'bot' && 'bg-purple-100'
              )}
            >
              {toast.type === 'alert' && <Bell className="w-4 h-4 text-blue-600" />}
              {toast.type === 'trade' && <TrendingUp className="w-4 h-4 text-green-600" />}
              {toast.type === 'bot' && <Bot className="w-4 h-4 text-purple-600" />}
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900">{toast.title}</p>
              <p className="text-sm text-gray-600 mt-1">{toast.message}</p>
            </div>

            <button
              onClick={() => removeToast(toast.id)}
              className="ml-2 p-1 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
